/**
 * Cache identity for workspace server state.
 *
 * Defined here as typed factories rather than inline arrays so invalidation and
 * prefetching cannot drift from the queries they target.
 */
export const workspaceKeys = {
  all: () => ['workspaces'] as const,
  /**
   * The collection itself. Distinct from any single workspace's detail.
   *
   * The page size is part of the identity: asking for 100 is a different
   * question from asking for 50, and caching both under one key would serve a
   * truncated answer to the larger request. Invalidation still targets the
   * whole collection — `['workspaces', 'list']` is a prefix of every variant.
   */
  list: (limit?: number) =>
    (limit === undefined
      ? ['workspaces', 'list']
      : ['workspaces', 'list', limit]) as readonly unknown[],
  /**
   * One workspace's aggregate.
   *
   * `includeHidden` is part of the identity for the same reason `list`'s page size is:
   * asking for the board is a different question from asking for the board plus its
   * hidden tasks, and caching both under one key would serve one answer to the other's
   * question. `['workspaces', id]` remains a prefix of both, so invalidation still
   * reaches them together.
   */
  detail: (workspaceId: string, includeHidden = false) =>
    (includeHidden
      ? ['workspaces', workspaceId, 'with-hidden']
      : ['workspaces', workspaceId]) as readonly unknown[],
  /**
   * Why the last first read of a workspace was refused (spec 188): its code, set by
   * that read itself. Outside the `['workspaces', id]` prefix on purpose — it is not
   * server state to refetch when the workspace is invalidated, only the reason the
   * read before came back empty.
   */
  refusal: (workspaceId: string) =>
    ['workspace-refusal', workspaceId] as readonly unknown[],
};
